import { useContext, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ReactflowInstanceContext } from '@src/scenario-editor/ScenarioEditor';
import { reactFlowInstanceAtom, getOpenedInfoModalCounter } from '@src/atoms';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { CustomTooltip } from '@src/components/tooltip/Tooltip';
import { updateNode } from '@src/scenario-editor/utils';
import { getSliderSchema } from '@src/scenario-editor/validator/schemas/slider';
import { SliderPreview } from '@src/components/slider-preview/SliderPreview';
import { SwitchComponent } from '@src/components/switch/Switch';
import { MultiOptionsSelector, type Option } from '@src/components/multi-options/MultiOptionsSelector';
import type { NodeToRenderProps, CustomSliderNodeData } from '@src/components/custom-nodes/types';

// LF360-1823 (#118): text-red-500 - сирий Tailwind-колір; --destructive у темі має те саме
// значення в світлій темі (hsl(0 84.2% 60.2%) === #EF4444), тож візуально це no-op, але
// іконка починає адаптуватися до темної теми. size-4 - ті самі 16px, що й решта іконок.
interface SliderNodeInfoProps {
  selectedNodeProps: NodeToRenderProps;
  disabled?: boolean;
  isPreview?: boolean;
}

interface SliderFormValues {
  questionId: string;
  message: string;
  description: string;
  minLabel: string;
  maxLabel: string;
  min: string;
  max: string;
  increment: string;
  response: Option[];
  required: boolean;
}

const toNum = (v: string): number => {
  if (typeof v === 'string' && v.trim() !== '' && /^-?\d+$/.test(v.trim())) {return Number(v.trim());}
  return NaN;
};

export const SliderNodeInfo = ({ selectedNodeProps, isPreview = false }: SliderNodeInfoProps) => {
  const data = (selectedNodeProps?.data ?? {}) as CustomSliderNodeData;
  const reactFlowInstance = useContext(ReactflowInstanceContext);
  const instance = reactFlowInstance.get(reactFlowInstanceAtom)!;
  const updateItem = updateNode({ reactFlowInstance: instance });

  const form = useForm<SliderFormValues>({
    resolver: zodResolver(getSliderSchema()),
    mode: 'onChange',
    defaultValues: {
      questionId: data.questionId ?? '',
      message: data.message ?? '',
      description: data.description ?? '',
      minLabel: data.minLabel ?? '',
      maxLabel: data.maxLabel ?? '',
      min: data.min !== undefined ? String(data.min) : '0',
      max: data.max !== undefined ? String(data.max) : '10',
      increment: data.increment !== undefined ? String(data.increment) : '1',
      response: data.response ?? [],
      required: data.required ?? true,
    },
  });

  const values = form.watch();
  const isLabeled = Array.isArray(values.response) && values.response.length > 0;

  const shouldShowErrorWarnings = getOpenedInfoModalCounter(selectedNodeProps.id) > 1;

  useEffect(() => {
    const timer = setTimeout(() => {
      void form.trigger();
    }, 500);
    return () => clearTimeout(timer);
  }, [form]);

  useEffect(() => {
    const subscription = form.watch((next) => {
      updateItem?.(selectedNodeProps?.id ?? '', {
        questionId: next.questionId,
        message: next.message,
        description: next.description,
        minLabel: next.minLabel,
        maxLabel: next.maxLabel,
        min: next.min,
        max: next.max,
        increment: next.increment,
        response: next.response,
        required: next.required,
      } as Partial<CustomSliderNodeData>);
    });
    return () => subscription.unsubscribe();
  }, [form, selectedNodeProps?.id, updateItem]);

  const incrementDisabled = isPreview || !values.min?.trim() || !values.max?.trim();

  type StringField = { [K in keyof SliderFormValues]: SliderFormValues[K] extends string ? K : never }[keyof SliderFormValues];

  const field = (name: StringField, label: string, placeholder: string, opts?: { textarea?: boolean; disabled?: boolean }) => (
    <div className="grid grid-cols-[150px_minmax(200px,_1fr)] items-start py-2">
      <div className="pt-2 text-label-foreground">{label}</div>
      <Controller
        control={form.control}
        name={name}
        render={({ field: f, fieldState }) => (
          <div className="relative pl-2">
            {shouldShowErrorWarnings && fieldState.error ? (
              <div className="absolute -left-[1.3rem] top-[0.5rem]">
                <CustomTooltip content={fieldState.error.message ?? ''}>
                  <ExclamationTriangleIcon className="size-4 text-destructive" />
                </CustomTooltip>
              </div>
            ) : null}
            {opts?.textarea ? (
              <Textarea value={f.value} onChange={f.onChange} placeholder={placeholder} disabled={isPreview} />
            ) : (
              <Input value={f.value} onChange={f.onChange} placeholder={placeholder} disabled={opts?.disabled ?? isPreview} />
            )}
          </div>
        )}
      />
    </div>
  );

  return (
    <div className="max-h-[70vh] overflow-y-auto p-4 text-sm">
      {field('questionId', 'Question ID', 'Optional question ID')}
      {field('message', 'Message', 'Add message', { textarea: true })}
      <div className="grid grid-cols-[150px_minmax(200px,_1fr)] items-center py-2">
        <div className="text-label-foreground">Required</div>
        <Controller
          control={form.control}
          name="required"
          render={({ field: f }) => (
            <div className="pl-2">
              <SwitchComponent
                value={f.value}
                onValueChange={f.onChange}
                disabled={isPreview}
              />
            </div>
          )}
        />
      </div>
      {field('description', 'Description', 'Add description', { textarea: true })}

      {/* LF360-1777: text labels reuse the single/multi-select options editor.
          Non-empty => labeled mode; the numeric fields below are hidden. */}
      <div className="grid grid-cols-[150px_minmax(200px,_1fr)] items-start py-2">
        <div className="pt-2 text-label-foreground">Labels</div>
        <Controller
          control={form.control}
          name="response"
          render={({ field: f, fieldState }) => (
            <div className="relative pl-2">
              {shouldShowErrorWarnings && fieldState.error ? (
                <div className="absolute -left-[1.3rem] top-[0.5rem]">
                  <CustomTooltip content={fieldState.error.message ?? ''}>
                    <ExclamationTriangleIcon className="size-4 text-destructive" />
                  </CustomTooltip>
                </div>
              ) : null}
              <MultiOptionsSelector
                value={f.value ?? []}
                isPreview={isPreview}
                onValueChange={(opts) => f.onChange(opts)}
              />
            </div>
          )}
        />
      </div>

      {!isLabeled ? (
        <>
          {field('minLabel', 'Min Label', 'e.g. None')}
          {field('maxLabel', 'Max Label', 'e.g. Worst Imaginable')}
          {field('min', 'Min Value', '0')}
          {field('max', 'Max Value', '10')}
          {field('increment', 'Increment', '1', { disabled: incrementDisabled })}
        </>
      ) : null}

      <div className="mt-1 grid grid-cols-[150px_minmax(200px,_1fr)]">
        <div className="text-label-foreground">Preview</div>
        <div className="pl-2">
          <SliderPreview
            message={values.message}
            description={values.description}
            minLabel={values.minLabel}
            maxLabel={values.maxLabel}
            min={toNum(values.min)}
            max={toNum(values.max)}
            increment={toNum(values.increment)}
            labels={isLabeled ? values.response.map((o) => o.label) : undefined}
          />
        </div>
      </div>
    </div>
  );
};
